import { Typography, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useEffect, useState } from "react";

import {
  ReplayLoaderFrame,
  ReplayLoaderLabel,
  ReplayLoaderSvg,
  ReplayLoaderValue,
  ReplayLoadingBackdrop,
  ReplayLoadingContent,
  ReplayLoadingOverlay,
} from "@Components/game/session-results/feedback/styled-components";

interface IFeedbackReplayLoadingState {
  isComplete?: boolean;
  onComplete?: () => void;
}

const MIN_LOADING_PROGRESS = 18;
const MAX_LOADING_PROGRESS = 94;
const LOADER_SIZE = 148;
const LOADER_STROKE_WIDTH = 12;
const COMPLETE_PROGRESS_STEP_MS = 24;
const AMBIENT_PROGRESS_STEP_MS = 70;

const FeedbackReplayLoadingState = ({ isComplete = false, onComplete }: IFeedbackReplayLoadingState) => {
  const theme = useTheme();
  const [loadingProgress, setLoadingProgress] = useState(MIN_LOADING_PROGRESS);

  useEffect(() => {
    if (isComplete) {
      const intervalId = window.setInterval(() => {
        setLoadingProgress(current => {
          if (current >= 100) {
            return 100;
          }

          const step = Math.max(2, Math.ceil((100 - current) / 6));
          return Math.min(100, current + step);
        });
      }, COMPLETE_PROGRESS_STEP_MS);

      return () => window.clearInterval(intervalId);
    }

    const intervalId = window.setInterval(() => {
      setLoadingProgress(current => {
        if (current >= MAX_LOADING_PROGRESS) {
          return current;
        }

        if (current < 40) {
          return Math.min(MAX_LOADING_PROGRESS, current + 4);
        }

        if (current < 70) {
          return Math.min(MAX_LOADING_PROGRESS, current + 2);
        }

        return Math.min(MAX_LOADING_PROGRESS, current + 1);
      });
    }, AMBIENT_PROGRESS_STEP_MS);

    return () => window.clearInterval(intervalId);
  }, [isComplete, onComplete]);

  useEffect(() => {
    if (!isComplete || loadingProgress < 100 || !onComplete) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      onComplete();
    }, 160);

    return () => window.clearTimeout(timeoutId);
  }, [isComplete, loadingProgress, onComplete]);

  const loaderRadius = (LOADER_SIZE - LOADER_STROKE_WIDTH) / 2;
  const loaderCircumference = 2 * Math.PI * loaderRadius;
  const loaderOffset = loaderCircumference * (1 - loadingProgress / 100);

  return (
    <ReplayLoadingOverlay role="status" aria-label="Loading replay" isComplete={isComplete}>
      <ReplayLoadingBackdrop />

      <ReplayLoadingContent alignItems="center" justifyContent="center">
        <ReplayLoaderFrame>
          <ReplayLoaderSvg viewBox={`0 0 ${LOADER_SIZE} ${LOADER_SIZE}`}>
            <circle
              cx={LOADER_SIZE / 2}
              cy={LOADER_SIZE / 2}
              r={loaderRadius}
              fill="none"
              stroke={alpha(theme.palette.common.white, 0.14)}
              strokeWidth={LOADER_STROKE_WIDTH}
            />
            <circle
              cx={LOADER_SIZE / 2}
              cy={LOADER_SIZE / 2}
              r={loaderRadius}
              fill="none"
              stroke={theme.palette.dsPrimary.v300}
              strokeWidth={LOADER_STROKE_WIDTH}
              strokeLinecap="round"
              strokeDasharray={loaderCircumference}
              strokeDashoffset={loaderOffset}
              transform={`rotate(-90 ${LOADER_SIZE / 2} ${LOADER_SIZE / 2})`}
              style={{ transition: "stroke-dashoffset 180ms ease-out" }}
            />
          </ReplayLoaderSvg>

          <ReplayLoaderLabel spacing={0.25} alignItems="center">
            <ReplayLoaderValue component="span">
              <Typography variant="text3xl700" color="white">
                {loadingProgress} %
              </Typography>
            </ReplayLoaderValue>
          </ReplayLoaderLabel>
        </ReplayLoaderFrame>
      </ReplayLoadingContent>
    </ReplayLoadingOverlay>
  );
};

export default FeedbackReplayLoadingState;
